import * as THREE from 'three';
import { SIMA, SIMA_BOSS, SIMA_SIGN, STRATUM, type StratumId } from '@somnhold/content';
import { hexRing, hexToWorld, type DungeonLayout, type SimaRun, type Zone } from '@somnhold/sim';
import { veinTexture } from './ascuas.ts';
import { WORLD_PER_HEX } from './coords.ts';
import type { CryptBuild } from './crypt.ts';
import { Descent } from './deep.ts';
import type { GlowLayer } from './glow.ts';
import { emis } from '../materials.ts';
import { BANDS, LOOKS, MOTION, type Motes, type StratumLook } from './sima-looks.ts';
import { buildProps, Kit, roomSpots } from './sima-props.ts';
import type { Vfx } from './vfx.ts';

/*
 * The Chasm (la Sima, docs/SIMA.md): a floor at a time, each from another stratum, and each must look very different
 * from the last. How, and still cheap on a slow phone (docs/RENDIMIENTO.md):
 *
 * - ONE crypt material for all twelve strata. The stratum and the depth band go in as uniforms (seams, overlay,
 *   plates, facets, glow, beat, ink drips, ash, black glass veins, the void) and as the crypt mesh's vertex colours,
 *   never as defines: a define is another program, another compile on a phone. Its program is compiled once, when
 *   the view opens (`prime`), and serves every floor after.
 * - ONE props mesh per floor (engine/sima-props.ts): everything that stands in the stratum merged, colour, glow and
 *   gloss per vertex, one shared material. The trip hammers' heads are one instanced mesh (they fall).
 * - ONE decal mesh (instanced, one material) for every hazard on the floor: the water, the deep pools, rime, ink,
 *   webs, spores, fires, and every marked strike with the same red ring (the eruption's: one language to learn).
 * - ONE Points draw for the motes (embers, spores, snow, ash, fireflies, dust, drips, falling stones), one material,
 *   a sprite atlas made here.
 * - Light: the crypt's fixed ROOM_SLOTS lights and the party's torches; a stratum only changes their colour and
 *   intensity. Fog always on, same type; a stratum changes its colour, the gloom sign brings it in.
 * - Going down (`dress` on a new floor): the old floor's geometry is dropped, the new one built behind the descent's
 *   cut to black. Nothing compiles: the programs are the same.
 */

const K = WORLD_PER_HEX / 1000;
const TAU = Math.PI * 2;
const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);
const lum = (r: number, g: number, b: number): number => 0.2126 * r + 0.7152 * g + 0.0722 * b;

/* ---------- the crypt material ---------- */

const PARS = /* glsl */ `
uniform sampler2D tVein;
uniform float uSmTime;
uniform vec4 uVein;
uniform vec3 uSeamC;
uniform vec3 uGlintC;
uniform vec4 uSeamA;
uniform vec3 uSeamP;
uniform vec3 uGroutC;
uniform vec3 uFootC;
uniform vec3 uOverC;
uniform vec4 uOverA;
uniform vec3 uOverE;
uniform vec3 uCellC;
uniform vec4 uCellA;
uniform vec4 uMisc;
uniform vec4 uBand;
varying vec3 vSmW;`;

/**
 * Every stratum's floor and walls from the same lines: plates of the shared Voronoi map painted over (paper, iron),
 * seams that darken or glow (fire, violet glass, ice cracks, red veins), an overlay by noise (moss, silk, rime, ink,
 * verdigris, glowing patches), glow in the grout and at the walls' foot, facets that tilt the light, a slow beat; then
 * the band's: ink running down the walls, ash on everything that faces up, black glass veins, the dark of the void.
 */
const FRAG = /* glsl */ `
{
  vec3 wp = vSmW;
  vec3 wn = normalize(cross(dFdx(wp), dFdy(wp)));
  float up = smoothstep(0.75, 0.95, abs(wn.y));
  float side = 1.0 - up;
  float floorY = 1.0 - smoothstep(0.08, 0.2, wp.y);
  float flr = up * floorY;
  float topF = up * (1.0 - floorY);
  vec2 p = mix(vec2(wp.x + wp.z, wp.y * 1.6), wp.xz, up);
  float beat = 1.0 + uMisc.w * sin(uSmTime * uMisc.z);
  vec4 a = texture2D(tVein, p * uVein.x);
  vec4 b = texture2D(tVein, p * uVein.x * 2.3 + vec2(0.37, 0.61));
  float n = texture2D(tVein, p * uOverA.z + vec2(0.5, 0.2)).b;
  float n2 = texture2D(tVein, p * uOverA.z * 3.1 + vec2(0.13, 0.77)).b;
  float plate = step(uCellA.x, a.g) * smoothstep(0.035, 0.07, a.r) * flr;
  float writ = step(0.82, fract(dot(p, vec2(0.25, 1.0)) * 11.0)) * step(0.12, fract(p.x * 1.7 + a.g * 5.0)) * uCellA.w;
  diffuseColor.rgb = mix(diffuseColor.rgb, uCellC * (0.85 + 0.3 * b.b) * (1.0 - 0.55 * writ), plate * uCellA.y);
  float live = mix(1.0, smoothstep(0.42, 0.62, n * 0.6 + n2 * 0.4), uVein.z);
  float e1 = 1.0 - smoothstep(0.0, uVein.y, a.r);
  float e2 = (1.0 - smoothstep(0.0, uVein.y * 0.7, b.r)) * 0.5;
  float seam = max(e1, e2) * live * (flr + 0.6 * side + 0.6 * topF) * (1.0 - plate * uCellA.y);
  diffuseColor.rgb = mix(diffuseColor.rgb, uSeamP, seam * uSeamA.w);
  float tw = pow(max(0.0, sin(uSmTime * (0.3 + a.g * 0.5) * (0.5 + uSeamA.z) + a.g * 47.0 + b.g * 13.0)), 20.0);
  float hb = 0.75 + 0.25 * sin(uSmTime * (0.8 + a.g * 0.9) * uSeamA.z + a.g * 6.2831);
  vec3 sc = mix(uSeamC, uGlintC, step(0.86, b.g));
  totalEmissiveRadiance += sc * seam * (uSeamA.x * hb + uSeamA.y * tw) * beat;
  float cov = smoothstep(uOverA.w - 0.07, uOverA.w + 0.07, n * 0.65 + n2 * 0.35);
  float ov = cov * (flr * uOverA.x + (side + topF) * uOverA.y) * (1.0 - plate);
  diffuseColor.rgb = mix(diffuseColor.rgb, uOverC * (0.8 + 0.4 * n2), ov);
  totalEmissiveRadiance += uOverE * ov * (0.6 + 0.4 * sin(uSmTime * 1.3 + n * 31.0));
  float grout = (1.0 - smoothstep(-0.03, -0.02, wp.y)) * flr;
  totalEmissiveRadiance += uGroutC * grout * beat * live * live;
  float foot = side * (1.0 - smoothstep(-0.1, 0.35, wp.y)) * step(-0.35, wp.y);
  totalEmissiveRadiance += uFootC * foot * beat;
  if (uMisc.x >= 0.0) roughnessFactor = uMisc.x;
  metalnessFactor = max(metalnessFactor, uMisc.y);
  roughnessFactor = mix(roughnessFactor, 0.35, plate * uCellA.z);
  vec3 tilt = vec3(a.g - 0.5, 0.0, fract(a.g * 7.31 + 0.13) - 0.5) * uVein.w * up;
  normal = normalize(normal + (viewMatrix * vec4(tilt, 0.0)).xyz);
  if (uBand.x > 0.0) {
    float col = texture2D(tVein, vec2(p.x * 0.5, 0.31)).b;
    float len = texture2D(tVein, vec2(p.x * 0.5 + 0.5, 0.71)).b;
    float drip = side * smoothstep(0.56, 0.62, col) * step(0.95 - len * 1.1, wp.y) * step(-0.05, wp.y);
    float puddle = flr * smoothstep(0.62, 0.7, n2) * 0.8;
    float ink = max(drip, puddle) * uBand.x;
    diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.012, 0.008, 0.02), ink);
    roughnessFactor = mix(roughnessFactor, 0.2, ink);
    totalEmissiveRadiance += vec3(0.08, 0.03, 0.14) * ink * 0.25;
  }
  if (uBand.y > 0.0) {
    float ash = smoothstep(0.48, 0.72, n2 * 0.6 + b.b * 0.4) * up;
    diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.3, 0.295, 0.29) * (0.75 + 0.5 * n), ash * 0.85 * uBand.y);
    roughnessFactor = mix(roughnessFactor, 1.0, ash * uBand.y);
  }
  if (uBand.z > 0.0) {
    vec4 o = texture2D(tVein, p * 0.055 + vec2(0.71, 0.23));
    float v = (1.0 - smoothstep(0.0, 0.04, o.r)) * smoothstep(0.4, 0.58, o.b) * uBand.z;
    float tw2 = pow(max(0.0, sin(uSmTime * 0.7 + o.g * 40.0 + p.x * 0.6)), 16.0);
    diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.008, 0.006, 0.014), v);
    roughnessFactor = mix(roughnessFactor, 0.12, v);
    totalEmissiveRadiance += vec3(0.55, 0.32, 1.0) * v * (0.05 + 0.8 * tw2);
  }
  if (uBand.w > 0.0) {
    // The void: the walls fall away into black, a dull red far below catching their tops' broken edge.
    float rim = topF + side * smoothstep(0.1, 0.6, wp.y);
    diffuseColor.rgb *= 1.0 - 0.92 * uBand.w * rim;
    totalEmissiveRadiance += vec3(0.25, 0.03, 0.02) * uBand.w * side * smoothstep(0.45, 0.75, wp.y) * (1.0 - smoothstep(0.75, 0.95, wp.y)) * 0.25;
  }
}`;

/** The crypt's material made the Chasm's: one program for every stratum and band (see the header). */
const cryptMaterial = (src: THREE.MeshStandardMaterial, u: Record<string, THREE.IUniform>): THREE.MeshStandardMaterial => {
  const m = src.clone();
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, u);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vSmW;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvSmW = (modelMatrix * vec4(transformed, 1.0)).xyz;');
    shader.fragmentShader = shader.fragmentShader.replace('#include <common>', `#include <common>\n${PARS}`).replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>\n${FRAG}`);
  };
  m.customProgramCacheKey = () => 'somnhold-sima-crypt';
  m.needsUpdate = true;
  return m;
};

/** The props' material: vertex colours, and per vertex how much it glows (its own colour) and how glossy it is. */
const propsMaterial = (u: { uSmTime: THREE.IUniform }): THREE.MeshStandardMaterial => {
  const m = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.86, metalness: 0, flatShading: true });
  m.onBeforeCompile = (shader) => {
    shader.uniforms.uSmTime = u.uSmTime;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute vec2 aMat;\nvarying vec2 vMat;\nvarying float vSmP;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvMat = aMat;\nvSmP = (modelMatrix * vec4(transformed, 1.0)).x * 0.9;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform float uSmTime;\nvarying vec2 vMat;\nvarying float vSmP;')
      .replace('#include <roughnessmap_fragment>', '#include <roughnessmap_fragment>\nroughnessFactor = mix(roughnessFactor, 0.12, vMat.y);')
      .replace('#include <metalnessmap_fragment>', '#include <metalnessmap_fragment>\nmetalnessFactor = mix(metalnessFactor, 0.3, vMat.y);')
      .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\ntotalEmissiveRadiance += vColor.rgb * vMat.x * (0.85 + 0.15 * sin(uSmTime * 1.7 + vSmP));');
  };
  m.customProgramCacheKey = () => 'somnhold-sima-props';
  return m;
};

/* ---------- hazards on the floor: one instanced decal ---------- */

const D = { water: 0, deep: 1, rime: 2, ink: 3, web: 4, spore: 5, fire: 6, molten: 7, warn: 8, line: 9, cone: 10, pool: 11, crack: 12, scar: 13 } as const;
const MAX_DECALS = 96;

const DECAL_VERT = /* glsl */ `
attribute float aKind;
attribute vec3 aP;
varying vec2 vQ;
varying vec3 vP;
varying float vKind;
varying vec2 vSz;
void main() {
  vQ = position.xz;
  vP = aP;
  vKind = aKind;
  vSz = vec2(length(instanceMatrix[0].xyz), length(instanceMatrix[2].xyz));
  gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0);
}`;

/**
 * Premultiplied: what covers the floor writes alpha (water, ink, rime), what glows adds light with none (the warning
 * rings, spores, fire). A marked strike is always the eruption's ring: a hard red line on the radius, a ring closing
 * in from outside, a fill spreading from the middle as the countdown (vP.x 0 → 1) runs out.
 */
const DECAL_FRAG = /* glsl */ `
uniform sampler2D tVein;
uniform float uTime;
varying vec2 vQ;
varying vec3 vP;
varying float vKind;
varying vec2 vSz;
vec3 warnRing(float d, float p, float sd, vec2 q) {
  float blink = 0.6 + 0.4 * sin(uTime * (5.0 + 16.0 * p) + sd * 9.0);
  float line = exp(-pow((d - 0.9) / 0.03, 2.0)) * (0.9 + 0.6 * blink);
  float closeAt = 0.9 + (1.0 - p) * 0.5;
  float closing = exp(-pow((d - closeAt) / 0.045, 2.0)) * (0.35 + 0.65 * p) * (1.0 - step(0.999, p));
  float fill = (1.0 - smoothstep(p * 0.92 - 0.05, p * 0.92, d)) * (1.0 - smoothstep(0.87, 0.9, d));
  float base = (1.0 - smoothstep(0.86, 0.9, d)) * (0.12 + 0.25 * p);
  float tick = step(0.5, fract(atan(q.y, q.x) * 1.9099 - uTime * 0.6)) * exp(-pow((d - 0.96) / 0.022, 2.0)) * 0.7;
  vec3 red = vec3(1.0, 0.12, 0.05);
  return red * (line * 0.85 + closing * 0.8 + tick + base * blink) + vec3(0.55, 0.05, 0.01) * fill * (0.25 + 0.35 * p);
}
void main() {
  vec2 q = vQ;
  float d = length(q);
  float k = vKind;
  float p = vP.x;
  float sd = vP.y;
  vec3 col = vec3(0.0);
  float a = 0.0;
  vec2 w = q * vSz;
  float en = texture2D(tVein, vec2(atan(q.y, q.x) * 0.159 + sd, sd * 0.37 + 0.11)).b;
  if (k < 0.5) {
    // Shallow black water: the slabs show through, ripples catch the light.
    float edge = 1.0 - smoothstep(0.9, 1.0, d);
    vec4 n1 = texture2D(tVein, w * 0.09 + vec2(uTime * 0.011, uTime * 0.006));
    vec4 n2 = texture2D(tVein, w * 0.17 - vec2(uTime * 0.008, -uTime * 0.012));
    float rip = n1.b * 0.55 + n2.b * 0.45;
    float spec = pow(smoothstep(0.52, 0.86, rip), 3.0);
    vec2 cell = fract(w * 0.32 + sd) - 0.5;
    float ring = exp(-pow((length(cell) - fract(uTime * 0.35 + n1.g) * 0.45) / 0.02, 2.0)) * (1.0 - fract(uTime * 0.35 + n1.g));
    a = 0.7 * edge;
    col = vec3(0.004, 0.02, 0.02) * a + (vec3(0.3, 0.75, 0.66) * spec * 0.55 + vec3(0.5, 0.9, 0.8) * ring * 0.3) * edge;
  } else if (k < 1.5 || (k > 10.5 && k < 11.5)) {
    // A deep pool (and the leech mother's, its water level vP.x): black, rings going out, a wet rim.
    float lvl = k > 10.5 ? mix(0.45, 1.0, p) : 1.0;
    float r = d / (0.88 + 0.12 * en);
    float water = 1.0 - smoothstep(lvl * 0.86, lvl * 0.9, r);
    float mud = (1.0 - smoothstep(0.9, 1.0, r)) * (1.0 - water);
    float rings = exp(-pow((fract(r * 2.2 - uTime * 0.4) - 0.5) / 0.06, 2.0)) * water * (1.0 - r);
    float rim = exp(-pow((r - lvl * 0.88) / 0.03, 2.0));
    a = max(water * 0.92, mud * 0.55);
    col = vec3(0.0, 0.006, 0.008) * water * 0.92 + vec3(0.03, 0.035, 0.03) * mud * 0.55 + vec3(0.2, 0.6, 0.5) * (rings * 0.07 + rim * 0.3);
  } else if (k < 2.5) {
    // Rime: frost grown over the floor in crystals, sparkling.
    vec4 v = texture2D(tVein, w * 0.45 + sd);
    float r = d / (0.85 + 0.15 * en);
    float cov = 1.0 - smoothstep(0.75, 1.0, r);
    float edgeL = 1.0 - smoothstep(0.0, 0.08, v.r);
    float tw = pow(max(0.0, sin(uTime * 2.0 + v.g * 60.0)), 18.0);
    a = cov * (0.55 + 0.25 * v.b);
    col = mix(vec3(0.55, 0.75, 1.0), vec3(0.95, 0.98, 1.0), edgeL) * a * 0.85 + vec3(1.0) * tw * cov * 0.8;
  } else if (k < 3.5) {
    // Ink: a glossy black pool, ragged at its edge, a violet sheen.
    float wob = texture2D(tVein, w * 0.25 + sd + uTime * 0.01).b;
    float r = d / (0.8 + 0.2 * en + 0.08 * wob);
    float cov = 1.0 - smoothstep(0.88, 0.96, r);
    float sheen = pow(max(0.0, 1.0 - length(q - vec2(-0.25, 0.3)) * 1.6), 3.0);
    a = cov * 0.94;
    col = vec3(0.01, 0.005, 0.016) * a + vec3(0.35, 0.18, 0.6) * sheen * cov * 0.35;
  } else if (k < 4.5) {
    // A web: spokes and a spiral of white silk.
    float ang = atan(q.y, q.x);
    float spokes = exp(-pow(sin(ang * 4.0 + sd * 3.0) * d * 8.0, 2.0));
    float spiral = exp(-pow((fract(d * 5.0 + ang * 0.159) - 0.5) / 0.07, 2.0));
    float cov = 1.0 - smoothstep(0.85, 1.0, d);
    float lines = max(spokes, spiral * 0.8) * cov;
    a = lines * 0.75;
    col = vec3(0.9, 0.88, 0.98) * a + vec3(0.6, 0.5, 1.0) * lines * 0.08;
  } else if (k < 5.5) {
    // Spores: a cloud's glow on the floor, swirling.
    vec4 v = texture2D(tVein, w * 0.18 + vec2(uTime * 0.03, -uTime * 0.02) + sd);
    float g = (1.0 - smoothstep(0.2, 1.0, d)) * (0.55 + 0.45 * v.b);
    col = vec3(0.2, 0.8, 1.0) * g * 0.55;
    a = g * 0.1;
  } else if (k < 6.5) {
    // Embers on the floor: a scorch, fire licking in it.
    vec4 v = texture2D(tVein, w * 0.3 + sd + vec2(0.0, uTime * 0.04));
    float cov = 1.0 - smoothstep(0.6, 1.0, d);
    float fl = (1.0 - smoothstep(0.0, 0.15, v.r)) * (0.6 + 0.4 * sin(uTime * 7.0 + v.g * 30.0));
    a = cov * 0.5;
    col = vec3(0.02, 0.006, 0.004) * a + vec3(1.0, 0.35, 0.08) * (fl * 0.9 + 0.25) * cov * min(1.0, p * 2.0 + 0.3);
  } else if (k < 7.5) {
    // Molten metal: a white-hot heart, crust plates, a red rim.
    vec4 v = texture2D(tVein, w * 0.35 + sd + vec2(uTime * 0.01, 0.0));
    float r = d / (0.85 + 0.15 * en);
    float core = 1.0 - smoothstep(0.0, 0.8, r);
    float inside = 1.0 - smoothstep(0.86, 0.92, r);
    float seamL = 1.0 - smoothstep(0.0, 0.12, v.r);
    vec3 hot = mix(vec3(1.0, 0.25, 0.04), vec3(1.0, 0.75, 0.3), core);
    a = inside * 0.95;
    col = mix(vec3(0.05, 0.015, 0.01), hot * 1.4, max(seamL * 0.7, core * core)) * a + vec3(1.0, 0.18, 0.06) * exp(-pow((r - 0.89) / 0.03, 2.0)) * 0.7;
  } else if (k < 8.5) {
    // A marked strike.
    col = warnRing(d, p, sd, q);
    if (vP.z > 0.5) {
      // A scribe's rune: its glyph drawn in the ring, darker ink.
      float ang = atan(q.y, q.x);
      float star = exp(-pow(sin(ang * 2.5) * d * 6.0, 2.0)) * step(d, 0.75) * step(0.2, d);
      col += vec3(0.5, 0.15, 0.9) * star * (0.4 + 0.6 * p);
      a = (1.0 - smoothstep(0.84, 0.9, d)) * 0.35;
      col += vec3(0.01, 0.004, 0.02) * a;
    }
  } else if (k < 9.5) {
    // A beam's line between its crystals: idle a faint thread (vP.x < 0), marked the strike's red band filling in.
    float x = abs(q.x);
    float along = 1.0 - smoothstep(0.92, 1.0, abs(q.y));
    if (p < 0.0) {
      float thread = exp(-pow(x / 0.08, 2.0)) * along;
      col = vec3(0.75, 0.4, 1.0) * thread * (0.18 + 0.08 * sin(uTime * 3.0 + q.y * 6.0));
    } else {
      float blink = 0.6 + 0.4 * sin(uTime * (5.0 + 16.0 * p) + sd * 9.0);
      float edge = exp(-pow((x - 0.92) / 0.06, 2.0)) * along * (0.9 + 0.6 * blink);
      float fill = (1.0 - smoothstep(p * 0.9 - 0.08, p * 0.9, x)) * along;
      float core = exp(-pow(x / 0.1, 2.0)) * along * p;
      col = vec3(1.0, 0.12, 0.05) * (edge * 0.85 + 0.15 * along * blink) + vec3(0.55, 0.05, 0.01) * fill * (0.25 + 0.35 * p) + vec3(0.9, 0.5, 1.0) * core * 0.8;
    }
  } else if (k < 10.5) {
    // A cone (the bellows' breath): the warning's red edges, filling outward as the breath comes.
    float ang = abs(atan(q.x, q.y));
    float arc = vP.z;
    float inArc = 1.0 - smoothstep(arc - 0.02, arc, ang);
    float inR = 1.0 - smoothstep(0.97, 1.0, d);
    float edgeA = exp(-pow((ang - arc) * d / 0.025, 2.0)) * inR;
    float edgeR = exp(-pow((d - 0.985) / 0.02, 2.0)) * inArc;
    float blink = 0.6 + 0.4 * sin(uTime * (5.0 + 16.0 * p));
    float fill = (1.0 - smoothstep(p - 0.06, p, d)) * inArc * inR;
    col = vec3(1.0, 0.12, 0.05) * ((edgeA + edgeR) * (0.9 + 0.5 * blink) + 0.12 * inArc * inR * blink) + vec3(0.7, 0.12, 0.02) * fill * (0.3 + 0.4 * p);
  } else if (k < 12.5) {
    // A crack opening along the floor: the worm under it.
    float x = q.x * vSz.x;
    float j = (texture2D(tVein, vec2(q.y * vSz.y * 0.12 + sd, 0.4)).b - 0.5) * 0.5;
    float along = 1.0 - smoothstep(0.95, 1.0, abs(q.y));
    float c = exp(-pow((x - j) / 0.05, 2.0)) * along;
    float glow = exp(-pow((x - j) / 0.25, 2.0)) * along;
    a = c * 0.9;
    col = vec3(0.02, 0.005, 0.004) * a + vec3(1.0, 0.2, 0.06) * (c * 0.6 + glow * 0.25) * (0.6 + 0.4 * sin(uTime * 6.0));
  } else {
    // A scar where a hazard rests: cracks out of a dark middle.
    vec4 v = texture2D(tVein, w * 0.4 + sd);
    float cr = (1.0 - smoothstep(0.0, 0.05, v.r)) * (1.0 - smoothstep(0.3, 1.0, d));
    float mid = 1.0 - smoothstep(0.0, 0.35, d);
    a = max(cr, mid * 0.6) * 0.8;
    col = vec3(0.01, 0.008, 0.008) * a;
  }
  gl_FragColor = vec4(col, a);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

/* ---------- motes: one Points draw, one material, a sprite atlas ---------- */

let atlasTex: THREE.DataTexture | null = null;
/** 2×2 cells of 32²: a soft dot, a six-pointed flake, a drop, a ragged chunk (ash, grit). Made here, never downloaded. */
const atlas = (): THREE.DataTexture => {
  if (atlasTex) return atlasTex;
  const N = 64;
  const H = 32;
  const data = new Uint8Array(N * N * 4);
  let s = 0x2545f491;
  const rnd = (): number => {
    s = (Math.imul(s ^ (s >>> 15), 2246822519) + 0x9e3779b9) >>> 0;
    return s / 4294967296;
  };
  const jag = Array.from({ length: 12 }, () => 0.55 + rnd() * 0.4);
  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      const cell = (y >= H ? 2 : 0) + (x >= H ? 1 : 0);
      const u = ((x % H) + 0.5) / H - 0.5;
      const v = ((y % H) + 0.5) / H - 0.5;
      const d = Math.hypot(u, v) * 2;
      let a: number;
      if (cell === 0) a = Math.max(0, 1 - d) ** 1.6;
      else if (cell === 1) {
        const ang = Math.atan2(v, u);
        const arm = Math.exp(-((Math.sin(ang * 3) * d * 9) ** 2));
        a = Math.max(arm * Math.max(0, 1 - d), Math.max(0, 1 - d * 2.2)) ** 1.2;
      } else if (cell === 2) {
        const e = Math.hypot(u * 2.6, (v + (v > 0 ? v * 0.6 : 0)) * 1.1) * 2;
        a = Math.max(0, 1 - e) ** 0.8;
      } else {
        const ang = Math.atan2(v, u);
        const k = Math.floor(((ang + Math.PI) / TAU) * 12) % 12;
        a = d < (jag[k] as number) ? 0.95 : 0;
      }
      const o = (y * N + x) * 4;
      data[o] = data[o + 1] = data[o + 2] = 255;
      data[o + 3] = Math.round(Math.min(1, a) * 255);
    }
  }
  const t = new THREE.DataTexture(data, N, N, THREE.RGBAFormat);
  t.magFilter = THREE.LinearFilter;
  t.minFilter = THREE.LinearFilter;
  t.needsUpdate = true;
  atlasTex = t;
  return t;
};

const MOTE_VERT = /* glsl */ `
attribute vec4 aSeed;
attribute vec4 aLook;
attribute vec3 aCol;
uniform float uTime;
uniform float uPx;
varying float vA;
varying vec3 vC;
varying vec2 vCell;
varying float vSolid;
void main() {
  float t = uTime * (0.4 + aSeed.w * 0.5) + aSeed.z * 37.0;
  float life = fract(t * 0.06 + aSeed.z);
  vec3 p = vec3(aSeed.x, 0.0, aSeed.y);
  float m = aLook.x;
  float a = 1.0;
  if (m < 0.5) {
    p.y = life * 3.2;
    p.x += sin(t * 0.7 + aSeed.z * 6.28) * 0.4 + life * 0.8;
    p.z += cos(t * 0.53 + aSeed.w * 6.28) * 0.4;
    a = smoothstep(0.0, 0.08, life) * (1.0 - smoothstep(0.55, 1.0, life)) * (0.55 + 0.45 * sin(uTime * 11.0 + aSeed.z * 61.0));
  } else if (m < 1.5) {
    p.y = (1.0 - life) * 3.4;
    p.x += sin(t * 0.6 + aSeed.z * 6.28) * 0.35;
    p.z += cos(t * 0.45 + aSeed.w * 6.28) * 0.35;
    a = smoothstep(0.0, 0.1, life) * (1.0 - smoothstep(0.88, 1.0, life));
  } else if (m < 2.5) {
    p.y = 0.25 + aSeed.w * 2.0 + sin(t * 0.3) * 0.3;
    p.x += sin(t * 0.4 + aSeed.z * 6.28) * 0.7;
    p.z += cos(t * 0.33 + aSeed.w * 6.28) * 0.7;
    a = smoothstep(0.0, 0.15, life) * (1.0 - smoothstep(0.7, 1.0, life));
  } else if (m < 3.5) {
    p.y = 0.3 + aSeed.w * 1.6 + sin(t * 0.9) * 0.25;
    p.x += sin(t * 0.5 + aSeed.z * 6.28) * 0.9;
    p.z += cos(t * 0.41 + aSeed.w * 6.28) * 0.9;
    a = pow(max(0.0, sin(uTime * (1.3 + aSeed.z * 2.0) + aSeed.w * 40.0)), 4.0);
  } else if (m < 4.5) {
    float l2 = fract(uTime * (0.35 + aSeed.w * 0.2) + aSeed.z * 7.0);
    p.y = 2.6 * (1.0 - l2 * l2);
    a = step(0.04, l2) * (1.0 - smoothstep(0.92, 1.0, l2));
  } else {
    float l2 = fract(uTime * (0.18 + aSeed.w * 0.12) + aSeed.z * 7.0);
    p.y = 1.6 - l2 * l2 * 9.0;
    a = smoothstep(0.0, 0.05, l2) * (1.0 - smoothstep(0.75, 1.0, l2));
  }
  vec4 mv = modelViewMatrix * vec4(p, 1.0);
  vA = a;
  vC = aCol;
  float c = aLook.y;
  vCell = vec2(mod(c, 2.0), floor(c / 2.0)) * 0.5;
  vSolid = step(2.5, c);
  gl_PointSize = aLook.z * (0.6 + aSeed.w * 0.8) * uPx / max(0.5, -mv.z);
  gl_Position = projectionMatrix * mv;
}`;

const MOTE_FRAG = /* glsl */ `
uniform sampler2D tAtlas;
varying float vA;
varying vec3 vC;
varying vec2 vCell;
varying float vSolid;
void main() {
  float t = texture2D(tAtlas, vCell + vec2(gl_PointCoord.x, 1.0 - gl_PointCoord.y) * 0.5).a * vA;
  if (t < 0.01) discard;
  gl_FragColor = vec4(vC * t, t * mix(0.2, 1.0, vSolid));
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

/* ---------- the look ---------- */

export interface SimaLights {
  hemi: THREE.HemisphereLight | null;
  moon: THREE.DirectionalLight | null;
  rooms: THREE.PointLight[];
}

export interface SimaFx {
  vfx: Vfx;
  glow: GlowLayer;
  flare: (p: THREE.Vector3, color: string, size: number, life: number) => void;
  shake: (n: number) => void;
  low: boolean;
}

const premul = {
  transparent: true,
  depthWrite: false,
  blending: THREE.CustomBlending,
  blendSrc: THREE.OneFactor,
  blendDst: THREE.OneMinusSrcAlphaFactor,
  blendSrcAlpha: THREE.OneFactor,
  blendDstAlpha: THREE.OneMinusSrcAlphaFactor,
} as const;

const C = (hex: string, k = 1): THREE.Color => new THREE.Color(hex).multiplyScalar(k);

export class SimaLook {
  readonly group = new THREE.Group();
  /** The party's torches lean toward the stratum's light. */
  readonly torch = new THREE.Color('#ffb070');
  private u = {
    tVein: { value: veinTexture() },
    uSmTime: { value: 0 },
    uVein: { value: new THREE.Vector4() },
    uSeamC: { value: new THREE.Color() },
    uGlintC: { value: new THREE.Color() },
    uSeamA: { value: new THREE.Vector4() },
    uSeamP: { value: new THREE.Color() },
    uGroutC: { value: new THREE.Color() },
    uFootC: { value: new THREE.Color() },
    uOverC: { value: new THREE.Color() },
    uOverA: { value: new THREE.Vector4() },
    uOverE: { value: new THREE.Color() },
    uCellC: { value: new THREE.Color() },
    uCellA: { value: new THREE.Vector4() },
    uMisc: { value: new THREE.Vector4() },
    uBand: { value: new THREE.Vector4() },
  };
  private cryptMat: THREE.MeshStandardMaterial | null = null;
  private propsMat: THREE.MeshStandardMaterial;
  private props: THREE.Mesh;
  private heads: THREE.InstancedMesh;
  private anvils: THREE.InstancedMesh;
  private propsKey = '';
  private hammers: THREE.Vector3[] = [];
  private marks: { x: number; z: number; y: number; haz: string }[] = [];
  private decals: THREE.InstancedMesh;
  private dKind: THREE.InstancedBufferAttribute;
  private dP: THREE.InstancedBufferAttribute;
  private motes: THREE.Points;
  private moteU = { uTime: { value: 0 }, uPx: { value: 400 }, tAtlas: { value: atlas() } };
  private descent: Descent;
  /** Each zone's longest countdown seen (the fill runs 0 → 1 whatever the lead). */
  private warnMax = new Map<number, number>();
  private look: StratumLook = LOOKS.ossuary;
  private stratum: StratumId | '' = '';
  private rev = -1;
  private layout: DungeonLayout | null = null;
  private lights: SimaLights | null = null;
  private roomBase: number[] = [];
  private gloom = false;
  private primed = false;
  private fogBase = { near: 26, far: 52 };
  private pool = { x: 0, z: 0, r: 0, lvl: 1 };
  private tmp = new THREE.Vector3();
  private dir = new THREE.Vector3();

  constructor(
    private scene: THREE.Scene,
    private fx: SimaFx,
  ) {
    this.group.name = 'sima';
    this.propsMat = propsMaterial(this.u);
    this.props = new THREE.Mesh(new THREE.BufferGeometry(), this.propsMat);
    this.props.name = 'sima-props';
    this.props.receiveShadow = true;
    // The hammers' heads: iron blocks on a haft, one instance each (a twin material: instanced is another program).
    const hk = new Kit();
    hk.at('box', 0, -0.32, 0, 0, 0, 0, 0.42, 0.32, 0.3, '#2c2c31', 0, 0.7);
    hk.at('box', 0, -0.36, 0, 0, 0, 0, 0.46, 0.06, 0.34, '#ff3a12', 0.6, 0);
    hk.at('box', 0, 0, 0, 0, 0, 0, 0.07, 0.4, 0.07, '#2a221c');
    const headMat = propsMaterial(this.u);
    this.heads = new THREE.InstancedMesh(hk.geometry(), headMat, 12);
    this.heads.count = 0;
    this.heads.frustumCulled = false;
    this.heads.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    // The bellows' anvils come with it (sm.props, once its hall wakes): instances of the same material.
    const ak = new Kit();
    ak.at('anvil', 0, 0, 0, 0, 0, 0, 0.9, 0.9, 0.9, '#2e2e33', 0, 0.75);
    ak.at('box', 0, 0, 0, 0, 0.4, 0, 0.6, 0.06, 0.6, '#1a1010');
    this.anvils = new THREE.InstancedMesh(ak.geometry(), headMat, 8);
    this.anvils.count = 0;
    this.anvils.frustumCulled = false;
    // The decals.
    const quad = new THREE.PlaneGeometry(2, 2).rotateX(-Math.PI / 2);
    const dg = new THREE.InstancedBufferGeometry();
    dg.index = quad.index;
    dg.setAttribute('position', quad.getAttribute('position'));
    this.dKind = new THREE.InstancedBufferAttribute(new Float32Array(MAX_DECALS), 1).setUsage(THREE.DynamicDrawUsage);
    this.dP = new THREE.InstancedBufferAttribute(new Float32Array(MAX_DECALS * 3), 3).setUsage(THREE.DynamicDrawUsage);
    dg.setAttribute('aKind', this.dKind);
    dg.setAttribute('aP', this.dP);
    this.decals = new THREE.InstancedMesh(
      dg,
      new THREE.ShaderMaterial({
        uniforms: { tVein: this.u.tVein, uTime: this.u.uSmTime },
        vertexShader: DECAL_VERT,
        fragmentShader: DECAL_FRAG,
        ...premul,
        polygonOffset: true,
        polygonOffsetFactor: -4,
        polygonOffsetUnits: -8,
      }),
      MAX_DECALS,
    );
    this.decals.count = 0;
    this.decals.frustumCulled = false;
    this.decals.renderOrder = 1;
    this.decals.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.decals.name = 'sima-hazards';
    // The motes.
    this.motes = new THREE.Points(new THREE.BufferGeometry(), new THREE.ShaderMaterial({ uniforms: this.moteU, vertexShader: MOTE_VERT, fragmentShader: MOTE_FRAG, ...premul }));
    this.motes.frustumCulled = false;
    this.motes.renderOrder = 8;
    this.motes.name = 'sima-motes';
    const px = new THREE.Vector2();
    this.motes.onBeforeRender = (renderer) => {
      this.moteU.uPx.value = renderer.getDrawingBufferSize(px).y * 0.5;
    };
    // The crypt's floor is flat (height 0) everywhere.
    this.descent = new Descent(() => 0, fx.glow, fx.vfx, fx.low, SIMA.stairsRange, SIMA.chooseTicks);
    this.group.add(this.props, this.heads, this.anvils, this.decals, this.motes, this.descent.group);
    // What stands still keeps its matrix (the scene's walk skips it).
    for (const o of [this.props, this.decals, this.motes]) {
      o.updateMatrix();
      o.matrixAutoUpdate = false;
    }
    scene.add(this.group);
  }

  /** The floor whose crypt this was dressed for (-1: none yet). */
  get floorRev(): number {
    return this.rev;
  }

  /**
   * A floor was laid (`sm.rev`): dresses its crypt in the stratum's look and band, puts up its props, motes and lights.
   * `c` is the crypt built for `L`; its first material is swapped for the one shared by every floor.
   */
  dress(c: CryptBuild, L: DungeonLayout, sm: SimaRun, zones: readonly Zone[], lights: SimaLights): void {
    this.rev = sm.rev;
    this.layout = L;
    this.lights = lights;
    const look = LOOKS[sm.stratum];
    const band = BANDS[sm.band];
    this.look = look;
    this.stratum = sm.stratum;
    this.gloom = sm.signs.includes('gloom');
    this.paint(c, look);
    const old = c.mesh.material as THREE.MeshStandardMaterial;
    this.cryptMat ??= cryptMaterial(old, this.u);
    if (old !== this.cryptMat) {
      c.mesh.material = this.cryptMat;
      old.dispose();
    }
    this.uniforms(look, band.over);
    // Torches on the walls burn the stratum's fire; the hall's runes in its colour.
    const flameM = emis(look.flame[0], look.flame[1], 1.8, { transparent: true, opacity: 0.95, depthWrite: false });
    for (const f of c.flames) {
      (f.mesh as THREE.Mesh).material = flameM;
      f.glow.material.color.set(look.flame[1]);
    }
    c.runes.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) (m.material as THREE.MeshBasicMaterial).color.set(look.rune);
    });
    // The murk and the light.
    const sc = this.scene;
    sc.background = C(look.bg, band.over[3] > 0 ? 0.4 : 1);
    if (sc.fog instanceof THREE.Fog) {
      sc.fog.color.set(look.fog).multiplyScalar(band.over[3] > 0 ? 0.5 : 1);
      this.fogBase = { near: 26, far: 52 };
    }
    const li = band.light;
    if (lights.hemi) {
      lights.hemi.color.set(look.hemi[0]);
      lights.hemi.groundColor.set(look.hemi[1]);
      lights.hemi.intensity = look.hemi[2] * li;
    }
    if (lights.moon) {
      lights.moon.color.set(look.moon[0]);
      lights.moon.intensity = look.moon[1] * li;
    }
    this.roomBase = [];
    lights.rooms.forEach((l, i) => {
      const hall = L.rooms[i]?.kind === 'boss';
      const [col, k] = hall ? look.hall : look.room;
      l.color.set(col);
      l.intensity = k * li;
      this.roomBase.push(l.intensity);
    });
    this.torch.set('#ffb070').lerp(new THREE.Color(look.torch), 0.55);
    // Props, the trip hammers, the motes.
    const k = WORLD_PER_HEX / 1000;
    const avoid: [number, number][] = [];
    const hall = L.rooms[L.rooms.length - 1];
    if (hall) for (const t of [0, 0.5]) {
      // The stairs and the way out open on the hall's second ring (sim: simaHallCleared).
      const ring = hexRing(2).map((h) => ({ q: hall.q + h.q, r: hall.r + h.r }));
      const h = ring[Math.floor(ring.length * t)];
      if (h) {
        const w = hexToWorld(h.q, h.r);
        avoid.push([w.x * k, w.y * k]);
      }
    }
    const rooms = roomSpots(L, avoid);
    const emitters = zones.filter((z) => z.kind === 'sima' && z.warn === undefined && (z.every > 0 || z.haz === 'deep'));
    let seed = (sm.floor * 2654435761) >>> 0;
    const rnd = (): number => {
      seed = (Math.imul(seed ^ (seed >>> 15), 2246822519) + 0x9e3779b9) >>> 0;
      return seed / 4294967296;
    };
    const built = buildProps(sm.stratum, { rooms, rnd, low: this.fx.low, emitters, props: sm.props });
    this.props.geometry.dispose();
    this.props.geometry = built.geometry;
    this.hammers = built.hammers;
    this.marks = built.marks;
    this.heads.count = Math.min(12, this.hammers.length);
    this.buildMotes(L, rooms, [...look.motes, ...band.motes]);
    this.propsKey = '-';
    this.anvils.count = 0;
    this.warnMax.clear();
    this.decals.count = 0;
  }

  /** The crypt mesh's vertex colours, repainted in the stratum's stone (its light and shade kept). */
  private paint(c: CryptBuild, look: StratumLook): void {
    const geo = c.mesh.geometry;
    const col = geo.getAttribute('color') as THREE.BufferAttribute | undefined;
    const pos = geo.getAttribute('position') as THREE.BufferAttribute;
    if (!col) return;
    const fd = C(look.floor[0]), fl = C(look.floor[1]), gr = C(look.grout), wd = C(look.wall[0]), wl = C(look.wall[1]);
    const k = new THREE.Color();
    for (let i = 0; i + 2 < col.count; i += 3) {
      const top = Math.max(pos.getY(i), pos.getY(i + 1), pos.getY(i + 2));
      const wall = top > 0.3;
      for (let j = i; j < i + 3; j++) {
        const l = lum(col.getX(j), col.getY(j), col.getZ(j));
        if (wall) k.copy(wd).lerp(wl, clamp01((l - 0.005) / 0.05));
        else if (pos.getY(j) < -0.03 && top < -0.02) k.copy(gr);
        else k.copy(fd).lerp(fl, clamp01((l - 0.01) / 0.05));
        col.setXYZ(j, k.r, k.g, k.b);
      }
    }
    col.needsUpdate = true;
  }

  private uniforms(L: StratumLook, band: [number, number, number, number]): void {
    const u = this.u;
    u.uVein.value.set(...L.vein);
    u.uSeamC.value.set(L.seam);
    u.uGlintC.value.set(L.glint === '#000000' ? L.seam : L.glint);
    u.uSeamA.value.set(...L.seamA);
    u.uSeamP.value.set(L.seamPaint);
    u.uGroutC.value.set(L.grout2[0]).multiplyScalar(L.grout2[1]);
    u.uFootC.value.set(L.foot[0]).multiplyScalar(L.foot[1] * 0.12);
    u.uOverC.value.set(L.over);
    u.uOverA.value.set(...L.overA);
    u.uOverE.value.set(L.overGlow[0]).multiplyScalar(L.overGlow[1]);
    u.uCellC.value.set(L.cell);
    u.uCellA.value.set(L.cellA[0], L.cellA[1], L.cellA[2], this.stratum === 'archive' ? 1 : 0);
    u.uMisc.value.set(...L.misc);
    u.uBand.value.set(...band);
  }

  /** The motes over this floor's cells (and, in the abyss, beyond its walls where stones fall into the void). */
  private buildMotes(L: DungeonLayout, rooms: ReturnType<typeof roomSpots>, kinds: Motes[]): void {
    const cells = L.floor.map((h) => {
      const w = hexToWorld(h.q, h.r);
      return [w.x * K, w.y * K] as const;
    });
    const edges = rooms.flatMap((r) => r.faces.map((f) => [f.x - f.nx * 0.9, f.z - f.nz * 0.9] as const));
    let s = 0x51a4 ^ (this.rev * 7919);
    const rnd = (): number => {
      s = (Math.imul(s ^ (s >>> 15), 2246822519) + 0x9e3779b9) >>> 0;
      return s / 4294967296;
    };
    const total = kinds.reduce((t, m) => t + Math.round(m.n * (this.fx.low ? 0.5 : 1)), 0);
    const seed = new Float32Array(total * 4);
    const look = new Float32Array(total * 4);
    const col = new Float32Array(total * 3);
    let i = 0;
    const c = new THREE.Color();
    for (const m of kinds) {
      const n = Math.round(m.n * (this.fx.low ? 0.5 : 1));
      const from = m.motion === MOTION.plunge ? edges : cells;
      if (!from.length) continue;
      c.set(m.color).multiplyScalar(m.glow);
      for (let j = 0; j < n; j++, i++) {
        const sp = from[Math.floor(rnd() * from.length)] as readonly [number, number];
        seed.set([sp[0] + (rnd() - 0.5) * 1.1, sp[1] + (rnd() - 0.5) * 1.1, rnd(), rnd()], i * 4);
        look.set([m.motion, m.cell, m.size * 4.5, 0], i * 4);
        col.set([c.r, c.g, c.b], i * 3);
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(i * 3), 3));
    g.setAttribute('aSeed', new THREE.BufferAttribute(seed.subarray(0, i * 4), 4));
    g.setAttribute('aLook', new THREE.BufferAttribute(look.subarray(0, i * 4), 4));
    g.setAttribute('aCol', new THREE.BufferAttribute(col.subarray(0, i * 3), 3));
    this.motes.geometry.dispose();
    this.motes.geometry = g;
  }

  /** Each drawing: the hazards on the floor, the guardian's marks, the trip hammers, the stairs and the way out. */
  sync(sm: SimaRun, zones: readonly Zone[], tick: number, fresh: boolean): void {
    const L = this.layout;
    if (!L) return;
    const hall = L.rooms[L.rooms.length - 1] ?? null;
    this.descent.sync(sm, hall, tick, fresh);
    if (fresh) this.hazards(sm, zones, tick);
    this.glows(sm, zones, tick, fresh);
  }

  private progress(z: Zone): number {
    const w = z.warn ?? 0;
    const max = Math.max(this.warnMax.get(z.id) ?? 0, w, 1);
    this.warnMax.set(z.id, max);
    return clamp01(1 - (w - 1) / max);
  }

  /** The decals for this tick: one instance per hazard, the water first (it lies under the rest). */
  private hazards(sm: SimaRun, zones: readonly Zone[], tick: number): void {
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const e = new THREE.Euler();
    const v = new THREE.Vector3();
    const sc = new THREE.Vector3();
    const kinds = this.dKind.array as Float32Array;
    const ps = this.dP.array as Float32Array;
    let n = 0;
    const put = (kind: number, x: number, z: number, rx: number, rz: number, yaw: number, p: number, seed: number, extra = 0): void => {
      if (n >= MAX_DECALS) return;
      e.set(0, yaw, 0);
      q.setFromEuler(e);
      m.compose(v.set(x, 0.03 + (kind === D.water ? 0 : 0.004 + (n % 8) * 0.001), z), q, sc.set(rx, 1, rz));
      this.decals.setMatrixAt(n, m);
      kinds[n] = kind;
      ps[n * 3] = p;
      ps[n * 3 + 1] = seed;
      ps[n * 3 + 2] = extra;
      n++;
    };
    const R = (z: Zone): number => (z.radius / 1000) * WORLD_PER_HEX;
    const sd = (z: Zone): number => ((z.id * 0.6180339) % 1) * 7;
    for (const z of zones) if (z.kind === 'sima' && z.haz === 'water') put(D.water, z.x * K, z.y * K, R(z), R(z), 0, 0, sd(z));
    // What a guardian brings into its hall (the bellows' anvils, the leech mother's pool), as it comes.
    const pk = sm.props.map((o) => `${o.kind}${o.q},${o.r}`).join(';');
    if (pk !== this.propsKey) {
      this.propsKey = pk;
      const pool = sm.props.find((o) => o.kind === 'pool');
      this.pool = pool ? { x: pool.x * K, z: pool.y * K, r: (pool.r2 / 1000) * WORLD_PER_HEX, lvl: 1 } : { x: 0, z: 0, r: 0, lvl: 1 };
      const anv = sm.props.filter((o) => o.kind === 'anvil').slice(0, 8);
      anv.forEach((o, i) => {
        e.set(0, ((o.q * 7 + o.r * 13) % 8) * 0.4, 0);
        q.setFromEuler(e);
        m.compose(v.set(o.x * K, 0, o.y * K), q, sc.set(1, 1, 1));
        this.anvils.setMatrixAt(i, m);
      });
      this.anvils.count = anv.length;
      this.anvils.instanceMatrix.needsUpdate = true;
    }
    if (this.pool.r > 0) {
      const a = sm.act;
      const low = a?.kind === 'low' ? clamp01(Math.min(tick - a.from, a.until - tick) / 18) : 0;
      put(D.pool, this.pool.x, this.pool.z, this.pool.r, this.pool.r, 0, 1 - low, 0.3);
    }
    for (const z of zones) {
      if (z.kind !== 'sima') continue;
      const x = z.x * K, y = z.y * K, r = R(z);
      const h = z.haz;
      if (h === 'water') continue;
      if (z.warn !== undefined) {
        if (z.warn <= 0) continue;
        const p = this.progress(z);
        if (h === 'beam') {
          const tx = (z.tx ?? z.x) * K, ty = (z.ty ?? z.y) * K;
          put(D.line, (x + tx) / 2, (y + ty) / 2, r, Math.hypot(tx - x, ty - y) / 2, Math.atan2(tx - x, ty - y), p, sd(z));
        } else put(D.warn, x, y, r / 0.9, r / 0.9, 0, p, sd(z), h === 'ink' ? 1 : 0);
        continue;
      }
      if (h === 'deep') put(D.deep, x, y, r, r, 0, 1, sd(z));
      else if (h === 'rime') put(D.rime, x, y, r, r, sd(z), 1, sd(z));
      else if (h === 'ink') put(D.ink, x, y, r, r, sd(z), 1, sd(z));
      else if (h === 'web') put(D.web, x, y, r, r, sd(z), 1, sd(z));
      else if (h === 'spores') put(D.spore, x, y, r * 1.2, r * 1.2, 0, 1, sd(z));
      else if (h === 'embers') put(D.fire, x, y, r, r, sd(z), clamp01(z.ticks / 12), sd(z));
      else if (h === 'molten') put(D.molten, x, y, r, r, sd(z), 1, sd(z));
      else if (h === 'beam') put(D.line, ((z.x + (z.tx ?? z.x)) / 2) * K, ((z.y + (z.ty ?? z.y)) / 2) * K, r, (Math.hypot((z.tx ?? z.x) - z.x, (z.ty ?? z.y) - z.y) / 2) * K, Math.atan2((z.tx ?? z.x) - z.x, (z.ty ?? z.y) - z.y), -1, sd(z));
      else if (h === 'rockfall' || h === 'roots') put(D.scar, x, y, r * 0.8, r * 0.8, sd(z), 1, sd(z));
    }
    // The guardian's move under way: a swelling ring, the bellows' cone, the worm's crack.
    const a = sm.act;
    if (a) {
      const p = clamp01((tick - a.from) / Math.max(1, a.until - a.from));
      const ax = a.x * K, ay = a.y * K, ar = (a.r / 1000) * WORLD_PER_HEX;
      if (a.kind === 'swell') put(D.warn, ax, ay, ar / 0.9, ar / 0.9, 0, p, 0.5);
      // The cone's half-angle is the sim's own: `arc` angle steps (1024 a turn) each side of the aim (inSimaCone).
      // Read as degrees of a whole arc it drew 55° each side where the fire reaches 38.7°.
      else if (a.kind === 'inhale' || a.kind === 'blow') put(D.cone, ax, ay, ar, ar, Math.atan2(a.tx - a.x, a.ty - a.y), a.kind === 'blow' ? 1 : p, 0.5, SIMA_BOSS.bellows.arc * ((2 * Math.PI) / 1024));
      else if (a.kind === 'dive') {
        const tx = a.tx * K, ty = a.ty * K;
        put(D.crack, (ax + tx) / 2, (ay + ty) / 2, 0.6, Math.hypot(tx - ax, ty - ay) / 2, Math.atan2(tx - ax, ty - ay), p, 0.7);
      }
    }
    this.decals.count = n;
    this.decals.instanceMatrix.needsUpdate = true;
    this.dKind.needsUpdate = true;
    this.dP.needsUpdate = true;
    if (this.warnMax.size > 128) for (const id of [...this.warnMax.keys()].slice(0, 64)) this.warnMax.delete(id);
    // The trip hammers: raised at rest, higher as the strike is marked, down on the floor as it lands.
    if (this.hammers.length) {
      this.hammers.forEach((hp, i) => {
        if (i >= 12) return;
        let y = 1.45;
        for (const z of zones) {
          if (z.kind !== 'sima' || z.haz !== 'hammer' || z.warn === undefined) continue;
          if (Math.abs(z.x * K - hp.x) + Math.abs(z.y * K - hp.z) > 0.3) continue;
          y = z.warn > 0 ? 1.45 + 0.25 * this.progress(z) : 0.42;
        }
        m.compose(v.set(hp.x, y, hp.z), q.identity(), sc.set(1, 1, 1));
        this.heads.setMatrixAt(i, m);
      });
      this.heads.instanceMatrix.needsUpdate = true;
    }
  }

  /** Light on the floor (glow sprites, no lights): spores, fires and molten metal, niches and crystals while marked. */
  private glows(sm: SimaRun, zones: readonly Zone[], tick: number, fresh: boolean): void {
    const g = this.fx.glow;
    const low = this.fx.low;
    for (const z of zones) {
      if (z.kind !== 'sima') continue;
      const x = z.x * K, y = z.y * K, r = (z.radius / 1000) * WORLD_PER_HEX;
      if (z.haz === 'spores') {
        g.add(x, 0.6, y, SPORE, r * 2.2, 0.22);
        if (fresh && (tick + z.id) % (low ? 4 : 2) === 0) {
          const a = Math.random() * TAU;
          const d = Math.sqrt(Math.random()) * r;
          this.fx.vfx.burst(this.tmp.set(x + Math.cos(a) * d, 0.2 + Math.random() * 0.6, y + Math.sin(a) * d), { n: 1, color: '#7fe8ff', speed: 0.004, up: 0.01, size: 0.22, life: 22, gravity: -0.0005, spread: 0.1 });
        }
      } else if (z.haz === 'molten' || z.haz === 'embers') g.add(x, 0.15, y, FIRE, r * 1.8, 0.3);
      else if (z.haz === 'deep' && fresh && (tick + z.id) % 24 === 0) this.fx.vfx.ring(this.tmp.set(x, 0.05, y), r * 0.2, r * 0.8, '#5fd0b8', 12, 0.25);
      else if (z.warn !== undefined && z.warn > 0) {
        const p = this.progress(z);
        if (z.haz === 'niche' || z.haz === 'beam') {
          // The niche or the crystals the strike comes from flare red as it nears.
          for (const mk of this.marks) {
            const near = Math.hypot(mk.x - x, mk.z - y) < 1.2 || (z.haz === 'beam' && Math.hypot(mk.x - (z.tx ?? z.x) * K, mk.z - (z.ty ?? z.y) * K) < 1.2);
            if (mk.haz === z.haz && near) g.add(mk.x, mk.y, mk.z, z.haz === 'beam' ? BEAM : RED, 1.4 + p, 0.3 + 0.5 * p);
          }
        } else g.add(x, 0.1, y, RED, r * (0.6 + 1.0 * p), 0.12 + 0.3 * p);
        if (fresh && p > 0.5 && (tick + z.id) % 3 === 0) {
          if (z.haz === 'rockfall') this.fx.vfx.burst(this.tmp.set(x + (Math.random() - 0.5) * r, 2.6, y + (Math.random() - 0.5) * r), { n: 1, color: '#5a5048', speed: 0.002, up: -0.05, size: 0.1, life: 10, gravity: 0.01 });
          else if (z.haz === 'roots') this.fx.vfx.burst(this.tmp.set(x, 0.05, y), { n: 1, color: '#3c6e24', speed: 0.02, up: 0.04, size: 0.1, life: 8, gravity: 0.006, spread: r * 0.6 });
        }
      }
    }
    if (sm.act?.kind === 'swell') g.add(sm.act.x * K, 1, sm.act.y * K, SPORE, 3, 0.25);
    // The stratum's light pooled in the hall (it reads as the big room from far).
    const hall = this.layout?.rooms[this.layout.rooms.length - 1];
    if (hall) {
      const w = hexToWorld(hall.q, hall.r);
      g.add(w.x * K, 0.1, w.y * K, this.hallGlow.set(this.look.hall[0]), hall.radius * WORLD_PER_HEX * 1.4, 0.07);
    }
  }
  private hallGlow = new THREE.Color();

  /* ---------- events ---------- */

  /** A Chasm skill event (a guardian's move, the floor). Returns whether it was one. */
  skill(name: string, x: number, y: number): boolean {
    if (!name.startsWith('sima')) return false;
    const p = new THREE.Vector3(x * K, 0.05, y * K);
    const { vfx, low } = this.fx;
    const n = (k: number): number => (low ? Math.ceil(k / 2) : k);
    const tone = STRATUM[this.stratum || 'ossuary'].color;
    switch (name) {
      case 'simaFloor':
        // The new floor: its light rises round the party as the black lifts.
        vfx.ring(p, 0.3, 3.4, tone, 16, 0.7);
        vfx.burst(p.clone().setY(0.4), { n: n(18), color: tone, speed: 0.03, up: 0.03, size: 0.1, life: 22, gravity: -0.001, spread: 1.6 });
        return true;
      case 'simaCaptain':
        vfx.ring(p, 0.3, 2.6, '#ff4f3a', 12, 0.9);
        vfx.burst(p.clone().setY(0.3), { n: n(16), color: '#3a3430', speed: 0.06, up: 0.05, size: 0.4, life: 16, smoke: true, spread: 0.6 });
        this.fx.flare(p.clone().setY(1), tone, 2.6, 10);
        return true;
      case 'simaGuardian':
        vfx.ring(p, 0.4, 5.5, tone, 18, 1);
        vfx.ring(p, 0.3, 3.2, '#ff4f3a', 14, 0.8);
        vfx.shaft(p.clone().setY(0.02), p.clone().setY(5), tone, 14, 0.6);
        vfx.burst(p.clone().setY(0.5), { n: n(36), color: tone, speed: 0.08, up: 0.1, size: 0.12, life: 18, gravity: 0.002, spread: 1 });
        this.fx.flare(p.clone().setY(1.2), tone, 4.5, 12);
        this.fx.shake(0.3);
        return true;
      case 'simaLow':
        vfx.ring(p, 0.5, 3.2, '#5fd0b8', 16, 0.6);
        vfx.burst(p.clone().setY(0.2), { n: n(20), color: '#a8f4e4', speed: 0.06, up: 0.08, size: 0.1, life: 14, gravity: 0.008, spread: 1.4 });
        return true;
      case 'simaRise':
        vfx.burst(p.clone().setY(0.3), { n: n(30), color: '#a8f4e4', speed: 0.09, up: 0.12, size: 0.12, life: 16, gravity: 0.01, spread: 1.2 });
        vfx.ring(p, 0.3, 3.6, '#5fd0b8', 12, 0.8);
        this.fx.shake(0.15);
        return true;
      case 'simaRoots':
      case 'simaDive':
        vfx.burst(p.clone().setY(0.2), { n: n(18), color: name === 'simaRoots' ? '#2b1d13' : '#4a2a26', speed: 0.07, up: 0.08, size: 0.2, life: 14, smoke: true, spread: 0.6 });
        vfx.ring(p, 0.3, 2.2, name === 'simaRoots' ? '#7fc25a' : '#ff4f3a', 10, 0.7);
        this.fx.shake(0.12);
        return true;
      case 'simaSwell':
        vfx.implode(p.clone().setY(0.8), 3, { n: n(26), color: '#5fe0ff', size: 0.14, life: 30, swirl: 0.6, lift: 0.02 });
        return true;
      case 'simaIce':
        vfx.ring(p, 0.2, 1.6, '#cfe8ff', 10, 0.9);
        vfx.burst(p.clone().setY(0.6), { n: n(20), color: '#e8f4ff', speed: 0.05, up: 0.06, size: 0.08, life: 14, gravity: 0.006, spread: 0.5 });
        this.fx.flare(p.clone().setY(0.8), '#cfe8ff', 2, 8);
        return true;
      case 'simaBeams':
        this.fx.flare(p.clone().setY(1.6), '#e8a0ff', 3, 10);
        vfx.burst(p.clone().setY(1.4), { n: n(20), color: '#f0c0ff', speed: 0.06, up: 0.04, size: 0.08, life: 12, spread: 0.6 });
        return true;
      case 'simaRead':
      case 'simaPages':
        // Pages torn out and whirled up, grey as the archive.
        vfx.burst(p.clone().setY(1.2), { n: n(24), color: '#c4cad2', speed: 0.06, up: 0.08, size: 0.12, life: 22, gravity: 0.002, spread: 0.6 });
        vfx.ring(p, 0.3, 2.4, name === 'simaRead' ? '#9aa7b4' : '#8a5cff', 12, 0.7);
        return true;
      case 'simaReadCut':
        vfx.burst(p.clone().setY(1.2), { n: n(30), color: '#c4cad2', speed: 0.1, up: 0.05, size: 0.12, life: 18, gravity: 0.006, spread: 0.4 });
        vfx.ring(p, 0.2, 2.8, '#ff4f3a', 10, 0.9);
        this.fx.flare(p.clone().setY(1), '#ff8a6e', 2.4, 8);
        return true;
      case 'simaInhale':
        vfx.implode(p.clone().setY(0.8), 4, { n: n(30), color: '#ff7a3a', size: 0.1, life: 24, swirl: 0.2, lift: 0.01 });
        return true;
      case 'simaCocoon':
        vfx.burst(p.clone().setY(0.4), { n: n(16), color: '#f0ecfa', speed: 0.04, up: 0.05, size: 0.1, life: 16, gravity: 0.002, spread: 0.4 });
        vfx.ring(p, 0.2, 1.2, '#d9c8ff', 8, 0.6);
        return true;
      case 'simaSurface':
        // Dust, not red: coming up hurts no one (red on the floor is where a blow lands).
        vfx.ring(p, 0.4, 4.2, '#c4ccd8', 14, 0.7);
        vfx.burst(p.clone().setY(0.3), { n: n(40), color: '#4a2a26', speed: 0.12, up: 0.14, size: 0.3, life: 18, smoke: true, gravity: 0.01, spread: 1 });
        vfx.burst(p.clone().setY(0.3), { n: n(20), color: '#9a9ea6', speed: 0.1, up: 0.16, size: 0.12, life: 16, gravity: 0.014, spread: 0.6 });
        this.fx.shake(0.45);
        return true;
    }
    return false;
  }

  /** A Chasm burst (`boom` events). `zones`: the tick's, to tell which hazard a strike was. Returns whether it was one. */
  boom(kind: string, x: number, y: number, r: number, zones: readonly Zone[]): boolean {
    if (!kind.startsWith('sima')) return false;
    const p = new THREE.Vector3(x * K, 0.05, y * K);
    const { vfx, low } = this.fx;
    const n = (k: number): number => (low ? Math.ceil(k / 2) : k);
    switch (kind) {
      case 'simaHeld':
        vfx.ring(p, 0.15, 0.7, '#7fc25a', 10, 0.9);
        vfx.burst(p.clone().setY(0.15), { n: n(10), color: '#3c6e24', speed: 0.03, up: 0.06, size: 0.1, life: 12, gravity: 0.006, spread: 0.3 });
        return true;
      case 'simaFreeze':
      case 'simaIceBreak':
        vfx.burst(p.clone().setY(0.6), { n: n(kind === 'simaIceBreak' ? 26 : 14), color: '#dceeff', speed: 0.08, up: 0.06, size: 0.1, life: 14, gravity: 0.01, spread: 0.4 });
        vfx.ring(p, 0.2, 1.1, '#cfe8ff', 8, 0.8);
        this.fx.flare(p.clone().setY(0.7), '#e8f4ff', 1.6, 6);
        return true;
      case 'simaSleep':
        vfx.burst(p.clone().setY(1.4), { n: n(8), color: '#9fd8ff', speed: 0.01, up: 0.02, size: 0.12, life: 24, gravity: -0.001, spread: 0.2 });
        return true;
      case 'simaSpores':
        vfx.ring(p, r * K * 0.2, r * K, '#5fe0ff', 14, 0.7);
        vfx.burst(p.clone().setY(0.6), { n: n(30), color: '#7fe8ff', speed: 0.06, up: 0.03, size: 0.3, life: 26, gravity: -0.0008, spread: r * K * 0.5 });
        return true;
      case 'simaBeam': {
        // The beam lands: a bolt from crystal to crystal, white-violet.
        const z = zones.find((o) => o.kind === 'sima' && o.haz === 'beam' && o.x === x && o.y === y && (o.warn ?? 0) <= 0);
        const b = z ? new THREE.Vector3((z.tx ?? z.x) * K, 0.6, (z.ty ?? z.y) * K) : p.clone().setY(0.6);
        const a = p.clone().setY(0.6);
        vfx.beam(a, b, '#ffffff', 5, 0.12, 0.2);
        vfx.beam(a, b, '#e070ff', 8, 0.3, 0.6);
        this.fx.flare(a, '#e8a0ff', 2.4, 8);
        this.fx.flare(b, '#e8a0ff', 2.4, 8);
        for (let i = 0; i <= 4; i++) vfx.burst(a.clone().lerp(b, i / 4), { n: n(4), color: '#f0c0ff', speed: 0.05, up: 0.03, size: 0.08, life: 8, spread: 0.2 });
        this.fx.shake(0.08);
        return true;
      }
      case 'simaStrike': {
        const z = zones.find((o) => o.kind === 'sima' && o.x === x && o.y === y && o.warn === -1);
        const h = z?.haz ?? 'quake';
        const rr = (r / 1000) * WORLD_PER_HEX;
        if (h === 'hammer') {
          // Every strike's wave ends at its own line (past it, it read as a hit outside the mark).
          vfx.ring(p, 0.2, rr, '#ff8a3d', 8, 1);
          vfx.burst(p.clone().setY(0.2), { n: n(18), color: '#ffb060', speed: 0.1, up: 0.1, size: 0.07, life: 10, gravity: 0.012, spread: 0.2 });
          vfx.burst(p.clone().setY(0.2), { n: n(8), color: '#2a2422', speed: 0.05, up: 0.04, size: 0.4, life: 14, smoke: true, spread: 0.3 });
          this.fx.shake(0.2);
        } else if (h === 'rockfall') {
          vfx.burst(p.clone().setY(0.3), { n: n(14), color: '#5a5048', speed: 0.1, up: 0.07, size: 0.18, life: 12, gravity: 0.014, spread: rr * 0.5 });
          vfx.burst(p.clone().setY(0.3), { n: n(10), color: '#3a3230', speed: 0.06, up: 0.05, size: 0.5, life: 18, smoke: true, spread: rr * 0.5 });
          vfx.ring(p, 0.2, rr, '#9a9ea6', 9, 0.7);
          this.fx.shake(0.25);
        } else if (h === 'roots') {
          vfx.burst(p.clone().setY(0.2), { n: n(16), color: '#2b1d13', speed: 0.07, up: 0.12, size: 0.14, life: 12, gravity: 0.012, spread: rr * 0.6 });
          vfx.ring(p, 0.2, rr, '#7fc25a', 9, 0.9);
        } else if (h === 'ink') {
          vfx.burst(p.clone().setY(0.2), { n: n(14), color: '#3a1a6a', speed: 0.05, up: 0.06, size: 0.14, life: 14, gravity: 0.006, spread: rr * 0.5 });
          vfx.ring(p, 0.2, rr, '#8a5cff', 9, 0.9);
        } else {
          vfx.ring(p, 0.3, rr, '#ff4f3a', 10, 1);
          vfx.burst(p.clone().setY(0.3), { n: n(20), color: '#3a3230', speed: 0.1, up: 0.08, size: 0.35, life: 16, smoke: true, spread: rr * 0.5 });
          this.fx.shake(0.25);
        }
        return true;
      }
      case 'simaNiche':
        vfx.burst(p.clone().setY(0.4), { n: n(16), color: '#9aa0a8', speed: 0.06, up: 0.05, size: 0.35, life: 18, smoke: true, spread: 0.4 });
        vfx.burst(p.clone().setY(0.3), { n: n(10), color: '#c4c8ce', speed: 0.08, up: 0.08, size: 0.07, life: 10, gravity: 0.012, spread: 0.3 });
        vfx.ring(p, 0.2, 1.2, '#8fa3c0', 9, 0.7);
        return true;
      case 'simaDeep':
        vfx.burst(p.clone().setY(0.1), { n: n(18), color: '#a8f4e4', speed: 0.05, up: 0.1, size: 0.09, life: 12, gravity: 0.012, spread: 0.5 });
        vfx.ring(p, 0.2, (r / 1000) * WORLD_PER_HEX, '#5fd0b8', 12, 0.8);
        return true;
      case 'simaWeb':
        vfx.burst(p.clone().setY(0.3), { n: n(14), color: '#f0ecfa', speed: 0.05, up: 0.06, size: 0.06, life: 14, gravity: 0.004, spread: 0.5 });
        vfx.ring(p, 0.2, (r / 1000) * WORLD_PER_HEX, '#d9c8ff', 10, 0.7);
        return true;
      case 'simaEcho':
        vfx.burst(p.clone().setY(0.6), { n: n(14), color: '#3a2a5a', speed: 0.04, up: 0.05, size: 0.45, life: 20, smoke: true, spread: 0.3 });
        vfx.ring(p, 0.2, 1.2, '#8a5cff', 10, 0.7);
        return true;
    }
    return false;
  }

  /** The bellows' fire and the frost lady's breath: a cone of flame or frost. Returns whether it was one. */
  shot(kind: string, x: number, y: number, tx: number, ty: number): boolean {
    if (kind !== 'simaBlow' && kind !== 'frostBreath') return false;
    const fire = kind === 'simaBlow';
    const a = new THREE.Vector3(x * K, 0.9, y * K);
    const dir = this.dir.set((tx - x) * K, 0, (ty - y) * K);
    const len = dir.length() || 1;
    dir.multiplyScalar(1 / len);
    // The flames run as far as the fire burns (at 0.6 of it they told the back of the cone it was safe).
    const reach = ((fire ? SIMA_BOSS.bellows.range : SIMA_BOSS.frostLady.breath.range) / 1000) * WORLD_PER_HEX;
    // The sim's arc is in angle steps (1024 a turn) either side of the aim, as its cone test reads it.
    const half = (fire ? SIMA_BOSS.bellows.arc : SIMA_BOSS.frostLady.breath.arc) * ((2 * Math.PI) / 1024);
    const steps = this.fx.low ? 5 : 9;
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      const spread = Math.tan(half) * reach * t;
      for (const side of [-1, 0, 1]) {
        const p = a.clone().addScaledVector(dir, reach * t).add(this.tmp.set(-dir.z * spread * side * 0.6, -0.5 * t, dir.x * spread * side * 0.6));
        this.fx.vfx.burst(p, { n: 2, color: fire ? (i % 2 ? '#ffb060' : '#ff4a14') : i % 2 ? '#e8f4ff' : '#9fd0ff', speed: 0.03, up: fire ? 0.04 : 0.01, size: fire ? 0.35 : 0.25, life: 10 + i, gravity: fire ? -0.002 : 0.002, spread: spread * 0.3 });
      }
    }
    this.fx.flare(a, fire ? '#ff7a3a' : '#cfe8ff', 2.6, 8);
    if (fire) this.fx.shake(0.15);
    return true;
  }

  /* ---------- descent ---------- */

  /** The party arrived on a new floor (`to`): a cut to black, the camera jumps. */
  descend(to: THREE.Vector3): void {
    this.descent.descend(to);
  }

  takeJump(): THREE.Vector3 | null {
    return this.descent.takeJump();
  }

  /**
   * Compiles everything the Chasm can draw, the parts hidden until a hazard or a hall shows them included (the decals,
   * the hammers' heads, the stairs and the way out), once, as the view opens: a floor further down only swaps
   * geometry and uniforms, never a program. `compile` is the screen's (Post.compile: for its target, fenced).
   */
  prime(compile: (part?: THREE.Object3D) => Promise<unknown> | void, extra: THREE.Object3D[] = []): boolean {
    if (this.primed || this.rev < 0) return false;
    this.primed = true;
    const hidden: THREE.Object3D[] = [];
    const bare: [THREE.Mesh, THREE.BufferGeometry][] = [];
    const show = (o: THREE.Object3D): void => {
      o.traverse((x) => {
        if (!x.visible) {
          hidden.push(x);
          x.visible = true;
        }
        // A mesh with no geometry yet (the lava layer's, until there is lava) would compile another program:
        // three keys its programs on whether there is a position attribute (docs/RENDIMIENTO.md, «Trampas»).
        const m = x as THREE.Mesh;
        if (m.isMesh && !m.geometry.getAttribute('position')) {
          bare.push([m, m.geometry]);
          m.geometry = SPECK();
        }
      });
    };
    show(this.group);
    for (const o of extra) show(o);
    const counts = [this.decals.count, this.heads.count, this.anvils.count];
    this.decals.count = Math.max(1, this.decals.count);
    this.heads.count = Math.max(1, this.heads.count);
    this.anvils.count = Math.max(1, this.anvils.count);
    void compile(this.group);
    for (const o of extra) void compile(o);
    this.decals.count = counts[0] as number;
    this.heads.count = counts[1] as number;
    this.anvils.count = counts[2] as number;
    for (const o of hidden) o.visible = false;
    for (const [m, g] of bare) m.geometry = g;
    return true;
  }

  frame(now: number, camera: THREE.Camera): void {
    this.u.uSmTime.value = now % 3600;
    this.moteU.uTime.value = now % 3600;
    this.descent.frame(now);
    // The ribs' light beats slowly with the titan's heart.
    const L = this.look;
    if (L.misc[3] > 0 && this.lights) {
      const beat = 1 + L.misc[3] * 0.6 * Math.sin((now % 3600) * L.misc[2]);
      this.lights.rooms.forEach((l, i) => {
        l.intensity = (this.roomBase[i] ?? l.intensity) * beat;
      });
    }
    // The gloom sign: the fog comes in round where the camera looks (docs/SIMA.md: the lights are not touched).
    const fog = this.scene.fog;
    if (fog instanceof THREE.Fog) {
      if (this.gloom) {
        camera.getWorldDirection(this.dir);
        const t = this.dir.y < -0.05 ? -camera.position.y / this.dir.y : 20;
        const k = SIMA_SIGN.gloom.fogBp / 10000;
        fog.near = t * (0.25 + k * 0.5);
        fog.far = t * (0.6 + k * 1.1);
      } else {
        fog.near = this.fogBase.near;
        fog.far = this.fogBase.far;
      }
    }
  }

  dispose(): void {
    this.descent.dispose();
    this.props.geometry.dispose();
    this.heads.geometry.dispose();
    this.heads.dispose();
    this.anvils.geometry.dispose();
    this.anvils.dispose();
    this.decals.geometry.dispose();
    this.decals.dispose();
    this.motes.geometry.dispose();
    (this.motes.material as THREE.Material).dispose();
    (this.decals.material as THREE.Material).dispose();
    this.propsMat.dispose();
    (this.heads.material as THREE.Material).dispose();
    this.cryptMat?.dispose();
    this.scene.remove(this.group);
  }
}

let speck: THREE.BufferGeometry | null = null;
/** One tiny triangle far under the floor: a stand-in geometry for compiling a mesh that has none yet. */
const SPECK = (): THREE.BufferGeometry => {
  speck ??= new THREE.BufferGeometry().setAttribute('position', new THREE.Float32BufferAttribute([0, -60, 0, 0.001, -60, 0, 0, -60, 0.001], 3));
  return speck;
};

const SPORE = new THREE.Color('#4fd6ff');
const FIRE = new THREE.Color('#ff6a1f');
const RED = new THREE.Color('#ff4f3a');
const BEAM = new THREE.Color('#e070ff');


